import TableCell from '@material-ui/core/TableCell'
import { Stack } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CiClock1 } from 'react-icons/ci'
import { PiRulerThin } from 'react-icons/pi'

import { PolicyRule_RuleUnitsEnum, PolicyRule_VehicleTypesEnum } from 'src/api/gen/api'
import { IPolicyRuleMaintenance } from 'src/api/types/policy'
import { DistanceUnit } from 'src/api/types/units'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { PositiveIntegerField } from 'src/components/fields'
import { SimpleTable } from 'src/components/Table'
import { useAgencySettingsDistanceUnit } from 'src/layout/AgencySettings'

import { FieldTitle } from '../../commons'
import { RuleNameField, SwitchField, VehicleTypesField } from '../fields'

type Row = [
  TRulesValidation,
  string, // name
  string, // ruleId
  number, // minimum
  number, // maximum
  PolicyRule_RuleUnitsEnum, // rule_units
  PolicyRule_VehicleTypesEnum[] // vehicleTypes
]

function MaintenanceRule(props: Omit<IBaseTableProps<IPolicyRuleMaintenance>, 'allGeographies'>) {
  const { t } = useTranslation()
  const { value } = useAgencySettingsDistanceUnit()
  const distanceUnit = value === DistanceUnit.Kilometer ? PolicyRule_RuleUnitsEnum.km : PolicyRule_RuleUnitsEnum.miles

  const HEADERS = [
    t('rule.name'),
    t('rules.minimum.maintenance'),
    props.rules[0].rule_units === PolicyRule_RuleUnitsEnum.days
      ? t('rules.maximum.maintenance.duration')
      : t('rules.maximum.maintenance.distance', { unit: enumsTranslationKeys.Unit[props.rules[0].rule_units] }),
    t('rule.count.vehicle_types'),
  ]

  const handleChangeRule =
    (ruleId: string, key: keyof IPolicyRuleMaintenance) =>
    (val: string | number | PolicyRule_VehicleTypesEnum[]): void => {
      const newRules: IPolicyRuleMaintenance[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }
        return { ...rule, [key]: val }
      })
      props.handleChange(newRules)
    }

  const items = props.rules.map(
    (rule: IPolicyRuleMaintenance, idx): Row => [
      props.validation[idx],
      rule.name,
      rule.rule_id,
      rule.minimum,
      rule.maximum,
      rule.rule_units,
      rule.vehicle_types,
    ]
  )

  return (
    <>
      <SimpleTable
        headerRow={HEADERS.map((title: string) => (
          <TableCell key={`key-${title}`}>
            <FieldTitle title={title} />
          </TableCell>
        ))}
        items={items}
        rowRenderer={([ruleValidation, name, ruleId, minimum, maximum, ruleUnit, vehicleTypes]: Row) => (
          <>
            <TableCell style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
              <RuleNameField
                id="maintenance_fields_name"
                value={name}
                handleChange={handleChangeRule(ruleId, 'name')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('name')}
                helperText={ruleValidation.getFieldErrorMsg('name')}
              />
            </TableCell>

            <TableCell style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '100px' }} align="justify">
              <PositiveIntegerField
                id="maintenance_fields_minimum_duration"
                value={minimum}
                handleChange={handleChangeRule(ruleId, 'minimum')}
                disabled={props.disabled}
              />
            </TableCell>

            <TableCell style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '200px' }} align="justify">
              <Stack direction={'row'} alignItems={'flex-end'} justifyContent={'center'} height={'100%'}>
                <PositiveIntegerField
                  id="maintenance_fields_max_lapse"
                  value={maximum}
                  handleChange={handleChangeRule(ruleId, 'maximum')}
                  disabled={props.disabled}
                  strictly
                />
                <SwitchField<PolicyRule_RuleUnitsEnum>
                  value={ruleUnit}
                  handleChange={handleChangeRule(ruleId, 'rule_units')}
                  options={[
                    { value: PolicyRule_RuleUnitsEnum.days, icon: (iconProps) => <CiClock1 {...iconProps} /> },
                    { value: distanceUnit, icon: (iconProps) => <PiRulerThin {...iconProps} /> },
                  ]}
                  disabled={props.disabled}
                />
              </Stack>
            </TableCell>

            <TableCell style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '280px' }} align="justify">
              <VehicleTypesField
                id="maintenance_fields_vehicle_type"
                value={vehicleTypes}
                handleChange={handleChangeRule(ruleId, 'vehicle_types')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('vehicle_types')}
                helperText={ruleValidation.getFieldErrorMsg('vehicle_types')}
              />
            </TableCell>
          </>
        )}
      />
    </>
  )
}

export default MaintenanceRule
